import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges
} from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';

import {
  inferUiOffsetFromBackendSeconds,
  RelativeOffsetPeriod
} from 'app/core/date/seconds-to-period-and-unit';
import { digitsOnly } from 'helpers';
import { Field, Rule, SimpleQueryFields } from 'query-builder/query.interfaces';

import {
  FollowUpDueDateMode,
  FollowUpDueDateSpecial,
  FollowUpRulePayload,
  FollowUpScopeLegacy
} from './follow-up-flag-query-types';

const SECONDS_PER_DAY = 24 * 60 * 60;
const SECONDS_PER_WEEK = SECONDS_PER_DAY * 7;
const SECONDS_PER_MONTH = SECONDS_PER_DAY * 30;

const SCOPE_VALUES: ReadonlyArray<Exclude<FollowUpScopeLegacy, null>> = [
  'any_user',
  'current_user',
  'assigned_user'
];

/**
 * Custom "Follow Up Flag" query input (simple + complex / filter-group builders).
 *
 * Prior to AB-863 the "Flagged for Follow Up" field was powered by the reusable
 * `single_select_buttons` input component to toggle between "By You", "By Anyone", and "For You".
 * This was sent to the API as a string (`any_user` | `current_user` | `assigned_user`).
 *
 * AB-863 added the ability to filter follow ups more granularly, which this component
 * provides a UI for:
 *
 * - `assigned_to` — assignment filter ("any_user" default, or "current_user").
 * - `created_by` — creator filter ("any_user" default, or "current_user").
 * - `due_date` — optional:
 *   - `mode`: "any" | "having" | "not_having" | "within"
 *   - `special`: "past_due" | "after_now" | "today" when mode is "having"
 *   - `relative_offset`: seconds from now — for `having`, due_date >= (now + offset).to_date; for `within`,
 *     today <= due_date <= (now + offset).to_date; e.g. -86400 with `having` means due_date >= yesterday.
 *   - `date_after` / `date_before`: optional day bounds as `YYYY-MM-DD` (strict > / < on the server).
 *
 * ControlValueAccessor model is the ngx-query-builder / simple-query `Rule` object **by reference**;
 * outbound edits mutate the bound rule so hosts (which two-way bind the rule directly) keep the same
 * object reference and do not need merge helpers.
 */
@Component({
  selector: 'app-follow-up-flag-query-presenter',
  templateUrl: './follow-up-flag-query-presenter.component.html',
})
export class FollowUpFlagQueryPresenterComponent implements OnChanges {
  public static readonly FIELD_TYPE = 'follow_up_flag';

  /** Required for user search input */
  @Input() campaignId: string;
  /** Field configuration */
  @Input() field: Field;
  /** Name of the field */
  @Input() fieldName: SimpleQueryFields;
  /** Disabled state of the input */
  @Input() disabled: boolean;
  /** Bound rule (mutated in place). */
  @Input() rule: Rule;

  @Output() ruleEdited = new EventEmitter<void>();

  selectedScope: FollowUpScopeLegacy = null;
  selectedDueDateMode: FollowUpDueDateMode = null;
  selectedDueDateSpecial: (FollowUpDueDateSpecial | 'within') | null = null;
  relativeOffsetUnits: string | null = null;
  relativeOffsetPeriod: RelativeOffsetPeriod = 'days';

  constructor(private _cd: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rule']?.currentValue) {
      this.applyIncomingRule(this.rule);
      this._cd.markForCheck();
    }
  }

  onChangeScope(event: MatButtonToggleChange): void {
    if (SCOPE_VALUES.includes(event.value)) {
      this.selectedScope = this.selectedScope === event.value ? null : event.value;
    }
    this.pushModelToData();
    this.notifyRuleEdited();
  }

  onDueDateModeChanged(mode: FollowUpDueDateMode): void {
    this.selectedDueDateMode = mode;

    if (mode !== 'having') {
      this.selectedDueDateSpecial = null;
      this.relativeOffsetUnits = null;
    }

    this.pushModelToData();
    this.notifyRuleEdited();
  }

  onDueDateSpecialChanged(special: (FollowUpDueDateSpecial | 'within') | null): void {
    this.selectedDueDateSpecial = special;

    if (special !== 'within') {
      this.relativeOffsetUnits = null;
    }

    this.pushModelToData();
    this.notifyRuleEdited();
  }

  onRelativeOffsetUnitsChanged(input: string | null): void {
    this.relativeOffsetUnits = digitsOnly(input);
    this.pushModelToData();
    this.notifyRuleEdited();
  }

  onRelativeOffsetPeriodChanged(period: RelativeOffsetPeriod): void {
    this.relativeOffsetPeriod = period;
    this.pushModelToData();
    this.notifyRuleEdited();
  }

  private applyIncomingRule(rule: Rule): void {
    let op = (rule.operator as string) ?? 'include';
    if (op === '=') {
      op = 'include';
    }
    rule.operator = op;
    this.normalizeIncomingValue(rule.value);
  }

  private resetInternalState(): void {
    this.selectedScope = null;
    this.selectedDueDateMode = null;
    this.selectedDueDateSpecial = null;
    this.relativeOffsetUnits = null;
    this.relativeOffsetPeriod = 'days';
  }

  /**
   * Normalizes the incoming value to the internal state.
   *
   * It is possible for the incoming value to be in the legacy string format
   * (`any_user` | `current_user` | `assigned_user`). If so, normalize it so it
   * acts like the new hash format and is compatible with the internal state.
   */
  private normalizeIncomingValue(raw: unknown): void {
    this.resetInternalState();

    if (raw == null) {
      return;
    }

    // Legacy string format only supports the scope.
    if (typeof raw === 'string') {
      if (SCOPE_VALUES.includes(raw as Exclude<FollowUpScopeLegacy, null>)) {
        this.selectedScope = raw as FollowUpScopeLegacy;
      }
      return;
    }

    if (typeof raw !== 'object') {
      return;
    }

    const v = raw as Record<string, unknown>;

    // Legacy scope mapping:
    // - "For You"   => assigned_to = current_user
    // - "By You"    => created_by  = current_user
    // - "By Anyone" => no constraint (or explicit any_user values)
    if (v.assigned_to === 'current_user') {
      this.selectedScope = 'assigned_user';
    } else if (v.created_by === 'current_user') {
      this.selectedScope = 'current_user';
    } else {
      this.selectedScope = 'any_user';
    }

    const due = v.due_date as Record<string, unknown> | undefined;
    if (!due || typeof due !== 'object') {
      return;
    }

    const selectedMode = due.mode as FollowUpDueDateMode | undefined;
    if (selectedMode === 'having' || selectedMode === 'not_having' || selectedMode === 'any') {
      this.selectedDueDateMode = selectedMode;
    }

    const selectedSpecial = due.special;
    if (selectedSpecial === 'past_due' || selectedSpecial === 'after_now' || selectedSpecial === 'today' || selectedSpecial === 'within') {
      this.selectedDueDateSpecial = selectedSpecial;
    }

    const relativeOffset = due.relative_offset;
    if (typeof relativeOffset === 'number' && relativeOffset !== 0) {
      const inferred = inferUiOffsetFromBackendSeconds(relativeOffset);

      this.selectedDueDateSpecial = 'within';
      this.selectedDueDateMode = 'having';
      this.relativeOffsetPeriod = inferred.period;
      this.relativeOffsetUnits = String(inferred.units);
    }
  }

  private serializeRulePayload(): FollowUpRulePayload | null {
    const base: FollowUpRulePayload = {};

    // Legacy scope -> API hash mapping:
    // - "For You"   => assigned_to = current_user
    // - "By You"    => created_by  = current_user
    // - "By Anyone" => no constraint (represented here as created_by = any_user)
    switch (this.selectedScope) {
      case 'assigned_user':
        base.assigned_to = 'current_user';
        break;
      case 'current_user':
        base.created_by = 'current_user';
        break;
      case 'any_user':
      default:
        base.created_by = 'any_user';
        break;
    }

    const mode = this.selectedDueDateMode;

    if (mode == null || mode === 'any') {
      return Object.keys(base).length ? base : null;
    }

    if (mode === 'not_having') {
      return { ...base, due_date: { mode: 'not_having' } };
    }

    if (this.selectedDueDateSpecial === 'today') {
      return { ...base, due_date: { mode: 'having', special: 'today' } };
    }

    if (this.selectedDueDateSpecial === 'past_due') {
      return { ...base, due_date: { mode: 'having', special: 'past_due' } };
    }

    if (this.selectedDueDateSpecial === 'after_now') {
      return { ...base, due_date: { mode: 'having', special: 'after_now' } };
    }

    if (this.selectedDueDateSpecial === 'within') {
      const rawUnits = Number.parseInt(this.relativeOffsetUnits || '', 10);
      const amount = Number.isFinite(rawUnits) && rawUnits > 0 ? rawUnits : 1;

      let relativeOffset: number;

      switch (this.relativeOffsetPeriod) {
        case 'weeks':
          relativeOffset = amount * SECONDS_PER_WEEK;
          break;
        case 'months':
          relativeOffset = amount * SECONDS_PER_MONTH;
          break;
        default:
          relativeOffset = amount * SECONDS_PER_DAY;
      }

      return {
        ...base,
        due_date: {
          mode: 'within',
          relative_offset: relativeOffset
        }
      };
    }

    // "Having" with no special constraint means "has any due date".
    return { ...base, due_date: { mode: 'having' } };
  }

  private pushModelToData(): void {
    if (!this.rule) {
      return;
    }
    this.rule.field = (this.fieldName as string | undefined) || (this.rule.field as string | undefined) || 'follow_up_by';
    this.rule.operator = this.rule.operator || 'include';
    this.rule.value = this.serializeRulePayload();
  }

  private notifyRuleEdited(): void {
    this._cd.markForCheck();
    this.ruleEdited.emit();
  }
}
